import React, { useState } from 'react';
import './style.css';
import { Laugh, Smile, Meh, Frown, ArrowLeft, ArrowRight, MoveUp } from 'lucide-react';
import CalculateRequiredIncomeToAfford from '/src/services/CalculateRequiredIncomeToAfford';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import { Doughnut, Line } from 'react-chartjs-2';
import HomeValueVsRequiredIncomeChart from '../../HomeValueVsRequiredIncomeChart/HomeValueVsRequiredIncomeChart';
import LTVCalculators from '/src/services/LTVCalculators';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';

const LTVAttractivenessFutureLooking = ({
    currentStageData,
    originalValues,
    currentHomeValue,
    setCurrentHomeValue,
    marketGrowthRate,
    setMarketGrowthRate,
    userIncomeGrowthRate,
    setUserIncomeGrowthRate,
    projectedFutureIncome,
    futureIncomeRequired,
    futureHomeValues,
    incomeGaps,
    original_income_gaps,
    highest_income_level,
    allRequiredIncomesAbove200k,
    hasUserMadeChanges,
    handleReset
}) => {

    const introSentence = "As we said at the start, this home’s future value is dependent on buyers at that point in time being willing and able to pay. So when thinking about the future value of your investment, you cannot just <i>assume</i> a growth rate, you need to sanity check that against where buyers will be down the line."
    const introSentence2 = "Below you can see how market and area household conditions could change your pool of buyers. When the gap between the market value you are expecting and what buyers can afford gets too large, it means actually achieving that value is harder to achieve."

    let stage_4_intro_sentence_addition;
    if (allRequiredIncomesAbove200k) {
        stage_4_intro_sentence_addition = " Given that the required income for this home already leaves you targeting the highest end of the market, your risk is that the associated costs for this home causes the pool of potential buyers to shrink."
    } else {
        stage_4_intro_sentence_addition = " Given that the required income for this home is currently in a sustainable range, you are less at risk even if the costs of this home cause that pool of potential buyers shrinks a bit."
    }

    const stage_4_intro_conclusion = " With that in mind, instead of debating if the market will go up (which we examine when looking at what is happening in the market), let's ask <b><i>are there going to be enough buyers to keep pushing the market up?</i></b>"

    const stage_4_income_growth_intro_sentence = "Let's take the highest level of income we see in the area and project forward how it will grow over the next 10 years, assuming current trends hold."

    const stage_4_income_against_value_growth_intro_sentence = "As a property appreciates, the income required to afford it also grows. What we need to understand is whether household income in this area is going to grow fast enough to meet the rising requirements, or will we see a gap. Remember, in this section we aren't asking <i>if</i> the market will go up, we're testing our assumptions against the income of potential buyers."

    // console.log(`projectedFutureIncome:`, projectedFutureIncome)
    // console.log(`futureIncomeRequired:`, futureIncomeRequired)

    let stage_4_income_against_value_growth_sentence_0;
    let stage_4_income_against_value_growth_sentence_1;
    let stage_4_income_against_value_growth_sentence_2;
    if (!original_income_gaps.crossesAnyLevel) {
        stage_4_income_against_value_growth_sentence_0 = "As you can see, "
        stage_4_income_against_value_growth_sentence_1 = "the projected household income in this area never catches up to the income that would be required to afford this home. "

        if (original_income_gaps.overallTrend.direction === 'worsening') {
            stage_4_income_against_value_growth_sentence_2 = "Concerningly, the gap is widening, not decreasing. To give yourself the best chance of getting the higher values you want, taking action to lower recurring expenses is important, and we cover how in our recommendations."
        } else {
            stage_4_income_against_value_growth_sentence_2 = "Luckily, that gap is actually shrinking, which means that potential pool of buyers is growing. Still, the fact that the lines never meet up is a concern. "
        }
    } else {
        stage_4_income_against_value_growth_sentence_0 = "As you can see, "
        stage_4_income_against_value_growth_sentence_1 = "the projected household income in the area is in line with the costs of this home! "

        if (original_income_gaps.overallTrend.direction === 'worsening') {
            stage_4_income_against_value_growth_sentence_2 = "That said, it is concerning that the gap is widening, which means that the pool of potential buyers is shrinking. You'll need to monitor this closely."
        } else {
            stage_4_income_against_value_growth_sentence_2 = "On top of that, household income seems to be outpacing that required income, which increases your pool of potential buyers and certainly could mean higher values!"
        }
    }

    let stage_4_income_against_value_growth_user_inputs_sentence_1;
    let stage_4_income_against_value_growth_user_inputs_sentence_2;
    let stage_4_income_against_value_growth_user_inputs_sentence_3;
    if (hasUserMadeChanges && incomeGaps?.vsOriginal) {
        if (incomeGaps.vsOriginal.rating === 'worse') {
            stage_4_income_against_value_growth_user_inputs_sentence_1 = "Using your inputs, we're still seeing a risky picture. "

            if (incomeGaps.vsOriginal.comparisons.averageGrowthRate.better) {
                stage_4_income_against_value_growth_user_inputs_sentence_2 = "That said, the gap between expected household income and the required income to afford the home isn't growing quite as quickly. "
            } else {
                stage_4_income_against_value_growth_user_inputs_sentence_2 = "In fact, the gap between expected household income and the required income to afford the home is growing even faster than it was with the original inputs. "
            }
        } else {
            stage_4_income_against_value_growth_user_inputs_sentence_1 = "If your inputs hold true, then the picture we see is indeed better! "

            if (incomeGaps.vsOriginal.comparisons.averageGrowthRate.better) {
                stage_4_income_against_value_growth_user_inputs_sentence_2 = "Any gap that exists is shrinking, which means an increased buyer pool."
            } else {
                stage_4_income_against_value_growth_user_inputs_sentence_2 = "Still, the gap we were hoping to see shrink still isn't."
            }
        }

        if (incomeGaps.overallTrend.riskLevel === original_income_gaps.overallTrend.riskLevel) {
            if (incomeGaps.overallTrend.riskLevel === 'high') {
                stage_4_income_against_value_growth_user_inputs_sentence_3 = "However, even with improvement, we still see your risk as:"

            } else {

                stage_4_income_against_value_growth_user_inputs_sentence_3 = "With these changes, we still see your risk as:"
            }
        } else {
            stage_4_income_against_value_growth_user_inputs_sentence_3 = "With these changes, we now see your risk as:"
        }
    }

    const chartTitle = "Required Income to Afford Home in Future vs. Expected Household Income in the Area"

    return (
        <div className="ltv-attractiveness-content">
            <h2 className="ltv-attractiveness-title">{currentStageData.title}</h2>
            <div className="ltv-attractiveness-intro-sentences">
                <p className="ltv-attractiveness-description text-align-center" dangerouslySetInnerHTML={{ __html: introSentence }} />
                <p className="ltv-attractiveness-description text-align-center" dangerouslySetInnerHTML={{ __html: introSentence2 }} />
                <CurlyBraceDivider direction='up' color='var(--dark-grey)' />
            </div>

            <div className="ltv-attractiveness-highest-income-future-values-container">
                {/* <p className="ltv-attractiveness-description text-align-center"
                    dangerouslySetInnerHTML={{ __html: stage_4_income_against_value_growth_intro_sentence }}
                /> */}
                <div className="ltv-attractiveness-growth-inputs-row">
                    <div className="ltv-attractiveness-assumptions-column">
                        <span className="ltv-attractiveness-headline text-align-center">
                            If we were to assume...
                        </span>
                        <div className="ltv-attractiveness-assumptions">
                            <div className="ltv-attractiveness-input-group">
                                <label>Home Value Today</label>
                                <div className="ltv-attractiveness-input-wrapper">
                                    <input
                                        type="range"
                                        min={Math.round((originalValues.currentHomeValue / 2) / 25000) * 25000}
                                        max={Math.round((originalValues.currentHomeValue * 1.5) / 25000) * 25000}
                                        step={25000}
                                        value={currentHomeValue}
                                        onChange={(e) => {
                                            setCurrentHomeValue(Number(e.target.value));
                                        }}
                                        className="ltv-attractiveness-slider"
                                    />
                                    <span className="ltv-attractiveness-input-value">
                                        {LTVCalculators.formatCurrency(currentHomeValue)}
                                    </span>
                                </div>
                            </div>
                            <div className="ltv-attractiveness-input-group">
                                <label>I Hope the Market Grows At...</label>
                                <div className="ltv-attractiveness-input-wrapper">
                                    <input
                                        type="range"
                                        min={0}
                                        max={15}
                                        step={0.5}
                                        value={marketGrowthRate * 100}
                                        onChange={(e) => {
                                            setMarketGrowthRate(Number(e.target.value) / 100);
                                        }}
                                        className="ltv-attractiveness-slider"
                                    />
                                    <span className="ltv-attractiveness-input-value">
                                        {(marketGrowthRate * 100).toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                            <div className="ltv-attractiveness-input-group">
                                <label>Household Income Grows At...</label>
                                <div className="ltv-attractiveness-input-wrapper">
                                    <input
                                        type="range"
                                        min={0}
                                        max={15}
                                        step={0.1}
                                        value={userIncomeGrowthRate !== null
                                            ? userIncomeGrowthRate * 100
                                            : highest_income_level.growth_rate * 100
                                        }
                                        onChange={(e) => {
                                            setUserIncomeGrowthRate(Number(e.target.value) / 100);
                                        }}
                                        className="ltv-attractiveness-slider"
                                    />
                                    <span className="ltv-attractiveness-input-value">
                                        {userIncomeGrowthRate !== null
                                            ? (userIncomeGrowthRate * 100).toFixed(1)
                                            : (highest_income_level.growth_rate * 100).toFixed(1)
                                        }%
                                    </span>
                                </div>
                            </div>
                            {hasUserMadeChanges && (
                                <button
                                    className="ltv-attractiveness-reset-button"
                                    onClick={handleReset}
                                >
                                    Reset to Original Values
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="ltv-attractiveness-chart-column" key="chart-column-1">
                        <HomeValueVsRequiredIncomeChart
                            chartTitle={chartTitle}
                            key="home-value-chart-component"
                            projectedFutureIncome={projectedFutureIncome}
                            futureIncomeRequired={futureIncomeRequired}
                            futureHomeValues={futureHomeValues}
                        />
                    </div>
                </div>
            </div>
            <div className={`ltv-attractiveness-gap-analysis-container ${hasUserMadeChanges ? 'user-made-changes' : ''}`}>
                <div className="ltv-attractiveness-gap-analysis-column">
                    {/* {hasUserMadeChanges &&
                        <div className="ltv-attractiveness-gap-analysis-column-data-source-header">
                            <span>Original Values</span>
                        </div>
                    } */}

                    <div className="ltv-attractiveness-sentences">
                        <p className="text-align-center">{stage_4_income_against_value_growth_sentence_0} {stage_4_income_against_value_growth_sentence_1} {stage_4_income_against_value_growth_sentence_2}</p>
                        {/* <p className="text-align-center">Because of this, we put the risk of having trouble finding buyers at:</p> */}
                    </div>
                    {/* <div className="ltv-attractiveness-risk-assessment">
                        <span className={`ltv-attractiveness-risk-level risk-${original_income_gaps.overallTrend.riskLevel}`}>
                            {original_income_gaps.overallTrend.riskLevel.toUpperCase()}
                        </span>
                    </div> */}
                </div>
                {/* {(hasUserMadeChanges && incomeGaps?.overallTrend) &&
                    <div className="ltv-attractiveness-gap-analysis-column">
                        <div className="ltv-attractiveness-gap-analysis-column-data-source-header">
                            <span>With Your Inputs</span>
                        </div>

                        <div className="ltv-attractiveness-sentences">
                            <p className="text-align-center">{stage_4_income_against_value_growth_user_inputs_sentence_1} {stage_4_income_against_value_growth_user_inputs_sentence_2}</p>
                            <p className="text-align-center">{stage_4_income_against_value_growth_user_inputs_sentence_3}</p>
                        </div>
                        <div className="ltv-attractiveness-risk-assessment">
                            <span className={`ltv-attractiveness-risk-level risk-${incomeGaps.overallTrend.riskLevel}`}>
                                {incomeGaps.overallTrend.riskLevel.toUpperCase()}
                            </span>
                        </div>
                    </div>
                } */}
            </div>

        </div>
    );
};

export default LTVAttractivenessFutureLooking;